import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Input from './Input';
import Button from './Button';
import { authService } from '../services/authService';
import { useAuth } from '../context/AuthContext';

type AuthView = 'login' | 'register';
type AccountType = 'buyer' | 'organizer';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialView?: AuthView;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialView = 'login' }) => {
    const { login } = useAuth();

    const [view, setView] = useState<AuthView>(initialView);
    const [accountType, setAccountType] = useState<AccountType>('buyer');

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [contactNumber, setContactNumber] = useState('');

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setView(initialView);
            setErrorMessage(null);
        }
    }, [isOpen, initialView]);

    const isLogin = view === 'login';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage(null);
        setIsLoading(true);

        try {
            if (isLogin) {
                await login({
                    username: email,
                    password: password
                });

                onClose();

            } else {
                const backendRole = accountType === 'organizer' ? 'user-organizer' : 'user-client-buyer';

                await authService.register({
                    username: email,
                    email: email,
                    firstName,
                    lastName,
                    password,
                    organizationName,
                    contactNumber,
                    role: backendRole
                });

                alert('¡Usuario registrado con éxito! Ahora inicia sesión.');
                setView('login');
            }
        } catch (error: any) {
            console.error("Error en modal:", error);
            setErrorMessage(error.message || 'Credenciales incorrectas o error en el servidor.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isLogin ? "Iniciar Sesión" : "Crear cuenta"}
        >
            {!isLogin && (
                <div className="flex border-b border-[var(--st-border)] mb-6">
                    <button
                        type="button"
                        className={`st-tab ${accountType === 'buyer' ? 'active' : ''} flex-1`}
                        onClick={() => setAccountType('buyer')}
                    >
                        Comprador
                    </button>
                    <button
                        type="button"
                        className={`st-tab ${accountType === 'organizer' ? 'active' : ''} flex-1`}
                        onClick={() => setAccountType('organizer')}
                    >
                        Organizador
                    </button>
                </div>
            )}

            {errorMessage && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
                    {errorMessage}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                {!isLogin && (
                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label="Nombre"
                            placeholder="Juan"
                            required
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                        <Input
                            label="Apellido"
                            placeholder="Díaz"
                            required
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>
                )}

                <Input
                    label="Email" type="email" placeholder="tu@email.com" required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" /></svg>}
                />

                <Input
                    label="Contraseña" type="password" placeholder="••••••••" required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
                />

                {!isLogin && accountType === 'organizer' && (
                    <div className="animate-fade-in">
                        <Input label="Nombre de la Organización" placeholder="Mi Empresa S.A."
                               value={organizationName}
                               onChange={(e) => setOrganizationName(e.target.value)}/>
                        <Input label="Whatsapp de contacto" placeholder="+57 300 123 4567" type="tel"
                               value={contactNumber}
                               onChange={(e) => setContactNumber(e.target.value)}/>
                    </div>
                )}

                {isLogin && (
                    <div className="flex items-center justify-between text-sm mb-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" className="st-checkbox"/>
                            <span className="text-[var(--st-text-muted)]">Recordarme</span>
                        </label>
                        <button type="button" className="text-[var(--st-accent-primary)] hover:underline">
                            ¿Olvidaste tu contraseña?
                        </button>
                    </div>
                )}

                {!isLogin && (
                    <div className="mb-6">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="checkbox" className="st-checkbox mt-1" required />
                            <span className="text-sm text-[var(--st-text-muted)]">
                  Acepto los <a href="/" className="text-[var(--st-accent-primary)] hover:underline">términos y condiciones</a>
                </span>
                        </label>
                    </div>
                )}

                <Button
                    variant="primary"
                    className="st-btn st-btn-primary st-btn-lg w-full"
                    size="lg"
                    type="submit"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
              <span className="st-spinner st-spinner-sm border-white border-t-transparent"></span>
              Procesando...
            </span>
                    ) : (
                        isLogin ? "Iniciar Sesión" : "Crear cuenta"
                    )}
                </Button>
            </form>

            <div className="mt-6 text-center text-sm text-[var(--st-text-muted)]">
                {isLogin ? "¿No tienes cuenta? " : "¿Ya tienes cuenta? "}
                <button onClick={() => setView(isLogin ? 'register' : 'login')} className="text-[var(--st-accent-primary)] font-medium hover:underline">
                    {isLogin ? "Regístrate" : "Inicia sesión"}
                </button>
            </div>
        </Modal>
    );
};

export default AuthModal;